import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, RefreshCw, Loader2, Percent, PenLine } from "lucide-react";
import { api, BRL, formatDateBR } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CdiManualDialog } from "@/components/CdiManualDialog";

const pctFmt = (v) => Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 6 });

export const CaixaCdiCard = ({ onChanged, account = "caixa", label = "saldo do caixa" }) => {
  const [d, setD] = useState(null);
  const [pct, setPct] = useState("");
  const [ir, setIr] = useState("");
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState(null);
  const load = () => api.get(`/finance/cdi/${account}`).then((r) => { setD(r.data); setPct(String(r.data.pct)); setIr(String(r.data.ir_pct)); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (body) => {
    try { await api.put(`/finance/cdi/${account}`, body); await load(); onChanged?.(); toast.success("Rendimento atualizado"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); }
  };
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/finance/cdi/${account}/run`);
      toast.success(r.data.added ? `${r.data.added} dia(s) de rendimento lançado(s)` : "Já está em dia — o rendimento de hoje entra amanhã");
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao atualizar"); }
    finally { setBusy(false); }
  };
  if (!d) return null;
  return (
    <section className="bg-white rounded-2xl border border-emerald-200 p-5 card-shadow" data-testid="caixa-cdi-card">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-4 h-4 text-emerald-700" />
        <h2 className="font-heading text-base font-black text-slate-900 flex-1">Rendimento {pctFmt(d.pct)}% do CDI</h2>
        <Switch checked={d.enabled} onCheckedChange={(v) => save({ enabled: v })} data-testid="caixa-cdi-toggle" />
      </div>
      <p className="text-[11px] text-slate-500 mb-3">
        Todo dia (inclusive fim de semana e feriado), igual ao banco: a taxa oficial do CDI (Banco Central) é dividida pelos 365 dias do ano e o rendimento entra já descontado o Imposto de Renda, no próprio dia, sobre o {label}.
        {d.start_date && <> Rendendo desde {formatDateBR(d.start_date)}.</>}
      </p>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="rounded-xl bg-emerald-50 p-2.5"><p className="text-[9.5px] font-black uppercase text-emerald-700">No mês</p><p className="text-sm font-black tabular-nums text-emerald-800" data-testid="caixa-cdi-month">{BRL(d.this_month)}</p></div>
        <div className="rounded-xl bg-emerald-50 p-2.5"><p className="text-[9.5px] font-black uppercase text-emerald-700">Total</p><p className="text-sm font-black tabular-nums text-emerald-800" data-testid="caixa-cdi-total">{BRL(d.total)}</p></div>
        <div className="rounded-xl bg-slate-50 p-2.5"><p className="text-[9.5px] font-black uppercase text-slate-500">CDI do dia</p><p className="text-sm font-black tabular-nums text-slate-800">{d.last_rate != null ? `${pctFmt(d.last_rate)}%` : "—"}</p></div>
      </div>
      <div className="flex gap-2 items-center flex-wrap">
        <div className="relative w-28">
          <Input type="number" min="1" max="300" step="any" value={pct} onChange={(e) => setPct(e.target.value)} className="h-9 pr-7" data-testid="caixa-cdi-pct" />
          <Percent className="w-3.5 h-3.5 absolute right-2.5 top-2.5 text-slate-400" />
        </div>
        <Button variant="outline" onClick={() => save({ pct: Number(pct) })} disabled={Number(pct) === d.pct} className="h-9" data-testid="caixa-cdi-pct-save">Salvar %</Button>
        <div className="flex items-center gap-1.5 w-full sm:w-auto">
          <span className="text-[11px] font-bold text-slate-500">IR</span>
          <div className="relative w-24">
            <Input type="number" min="0" max="99" step="any" value={ir} onChange={(e) => setIr(e.target.value)} className="h-9 pr-7" data-testid="caixa-cdi-ir" />
            <Percent className="w-3.5 h-3.5 absolute right-2.5 top-2.5 text-slate-400" />
          </div>
          <Button variant="outline" onClick={() => save({ ir_pct: Number(ir) })} disabled={ir === "" || Number(ir) === d.ir_pct} className="h-9" data-testid="caixa-cdi-ir-save">Salvar IR</Button>
        </div>
        <Button onClick={run} disabled={busy || !d.enabled} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white ml-auto" data-testid="caixa-cdi-run">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Atualizar agora
        </Button>
        <Button variant="outline" onClick={() => setManual({})} className="h-9 w-full border-emerald-200 text-emerald-800" data-testid="caixa-cdi-manual">
          <PenLine className="w-4 h-4 mr-1" /> Lançar rendimento real
        </Button>
      </div>
      {d.items.length > 0 && (
        <ul className="divide-y divide-slate-100 mt-3 max-h-56 overflow-y-auto" data-testid="caixa-cdi-list">
          {d.items.map((r) => (
            <li key={r.id} className="py-2 flex items-center gap-2 text-xs">
              <span className="font-semibold text-slate-700 w-20">{formatDateBR(r.date)}</span>
              <span className="flex-1 text-slate-500 truncate">{r.manual ? "Valor real lançado" : `CDI ${pctFmt(r.cdi_rate)}% × ${pctFmt(r.pct)}%${r.ir_pct != null ? ` − IR ${pctFmt(r.ir_pct)}%` : ""} sobre ${BRL(r.base_balance)}`}</span>
              <span className="font-black tabular-nums text-emerald-700">+ {BRL(r.amount)}</span>
              <button onClick={() => setManual(r)} className="text-slate-300 hover:text-emerald-700" title="Corrigir valor" data-testid={`caixa-cdi-edit-${r.date}`}><PenLine className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
      <CdiManualDialog account={account} target={manual} onClose={() => setManual(null)} onSaved={() => { setManual(null); load(); onChanged?.(); }} />
    </section>
  );
};
